<template>
  <!-- Round container that clips Benjix -->
  <div class="benjix-round-container relative">
    <div class="absolute inset-0 bg-purple-100 opacity-30 rounded-full" />
    <svg
      ref="svgRef"
      width="232"
      height="196"
      viewBox="0 0 232 196"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      :style="{
        transform: `scale(${bodyScale * (isHovering ? 1.1 : 1)})`,
        transformOrigin: 'center'
      }"
      class="absolute inset-0 m-auto"
      @mousemove="handleMouseMove"
      @mouseleave="handleMouseLeave"
      @click="handleClick"
    >
      <!-- Animated body group -->
      <g :style="{ transform: `translate(${bodyOffset.x}px, ${bodyOffset.y}px) scale(0.7)`, transformOrigin: '116px 98px' }">
        <!-- Main blob shape -->
        <path
          d="M1.00034 95.4829L0.998383 95.4663C-0.991449 65.8663 9.82612 41.0214 29.265 24.1909C48.7103 7.35487 76.822 -1.49316 109.462 0.998535L109.499 1.00146L109.535 0.998535C185.694 -4.47713 230.989 53.9656 231 111.978C229.257 151.335 214.081 171.783 194.326 182.435C174.513 193.118 150.008 194 129.5 194H129.484L129.468 194.001C74.5144 197.498 42.4452 187.992 24.099 170.142C5.7528 152.292 1.00038 125.974 1.00034 95.5005V95.4829Z"
          fill="#8761F1"
          stroke="#4416CE"
        />

        <!-- Left eye shadow -->
        <path
          d="M84 50.5C98.6025 50.5 110.5 63.2256 110.5 79C110.5 94.7744 98.6025 107.5 84 107.5C69.3975 107.5 57.5 94.7744 57.5 79C57.5 63.2256 69.3975 50.5 84 50.5Z"
          fill="#4B338F"
          stroke="#4B338F"
        />

        <!-- Light reflection -->
        <path
          d="M66.5 7.00002C27.3 16.6 10.5 47 7 61C-0.200001 80.2 5.33333 118.667 9 135.5C11.1667 135.667 15.6 135.8 16 135C16.4 134.2 16.5 133.333 16.5 133C3 25.5 68 13.5 69 13C69.8 12.6 70 11.8333 70 11.5L69 8.50002C69 6.90002 67.3333 6.83336 66.5 7.00002Z"
          fill="#C7AFFE"
          stroke="#845EF1"
        />

        <!-- Mouth -->
        <path
          d="M177 112.5C95.4 122.9 56.6667 117.5 47.5 113.5C43.1 111.9 42 116.5 42 119C52 141.5 82.7873 144.593 95.5 146.5C105.5 148 124.333 145.333 131.5 143.5C137.667 141.5 151.3 136.9 156.5 134.5C163 131.5 183 119 182 115.5C181.2 112.7 178.333 112.333 177 112.5Z"
          fill="#172132"
          stroke="#664EAD"
        />
        <path
          d="M100 136.5C97.9855 139.909 107.833 144 113.5 146C129 146 164.9 135.5 180.5 117.5C168 122.5 106.5 125.5 100 136.5Z"
          fill="#583FAE"
        />

        <!-- Left eye white -->
        <path
          d="M84 46.5C98.6025 46.5 110.5 59.2256 110.5 75C110.5 90.7744 98.6025 103.5 84 103.5C69.3975 103.5 57.5 90.7744 57.5 75C57.5 59.2256 69.3975 46.5 84 46.5Z"
          fill="white"
          stroke="#6E46EB"
        />

        <!-- Right eye shadow -->
        <path
          d="M140 50.5C154.602 50.5 166.5 63.2256 166.5 79C166.5 94.7744 154.602 107.5 140 107.5C125.398 107.5 113.5 94.7744 113.5 79C113.5 63.2256 125.398 50.5 140 50.5Z"
          fill="#4B338F"
          stroke="#4B338F"
        />

        <!-- Right eye white -->
        <path
          d="M140 46.5C154.602 46.5 166.5 59.2256 166.5 75C166.5 90.7744 154.602 103.5 140 103.5C125.398 103.5 113.5 90.7744 113.5 75C113.5 59.2256 125.398 46.5 140 46.5Z"
          fill="white"
          stroke="#6E46EB"
        />

        <!-- Shadow/depth -->
        <path
          d="M58.5 185C20.9 171.4 10.5 146.5 5.5 137C10.5 155 21.1667 169.833 32 176.5C35.0847 179.199 44.7595 184.203 55 187.5C66.0058 191.043 77.6652 192.809 82.5 193.5L102.5 194.5L144.5 193.5C167.5 192 191.498 187.712 206.5 173.5C225.5 155.5 237.5 122 226 79.4999C222.012 64.7623 214.354 52.5914 207.5 43.5C192 22 174.5 15 171 13C166.276 10.3007 158.333 7.16667 154 6L142 3C185.5 20.5 202 44.4999 218.5 88.9999C231.7 124.6 209.667 158.833 197 171.5C191.833 176.333 176.3 186.5 155.5 188.5C134.7 190.5 67 187 58.5 185Z"
          fill="#6945D5"
        />
      </g>

      <!-- Animated pupils -->
      <g>
        <!-- Left pupil -->
        <circle
          :cx="leftPupilX"
          :cy="leftPupilY"
          r="8"
          fill="#172132"
          class="transition-all duration-150 ease-out"
        />
        <circle
          :cx="leftPupilX - 2"
          :cy="leftPupilY - 2"
          r="3"
          fill="white"
          opacity="0.8"
          class="transition-all duration-150 ease-out"
        />

        <!-- Right pupil -->
        <circle
          :cx="rightPupilX"
          :cy="rightPupilY"
          r="8"
          fill="#172132"
          class="transition-all duration-150 ease-out"
        />
        <circle
          :cx="rightPupilX - 2"
          :cy="rightPupilY - 2"
          r="3"
          fill="white"
          opacity="0.8"
          class="transition-all duration-150 ease-out"
        />
      </g>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

// Component now uses CSS for sizing, no props needed

const svgRef = ref<SVGSVGElement | null>(null)
const mouseX = ref(0)
const mouseY = ref(0)
const isHovering = ref(false)
const isTracking = ref(false)
const isAnimationReady = ref(true) // Start ready to avoid delays
const bodyOffset = ref({ x: 0, y: 0 })
const bodyScale = ref(1) // Start at normal scale
const naturalEyeOffset = ref({ x: 0, y: 0 })
let animationId: number | null = null
let clickAnimationId: number | null = null
let lastInteractionTime = Date.now()
let animationStartTime = Date.now()

// Eye centers in SVG coordinates
const leftEyeCenter = { x: 90, y: 75 }
const rightEyeCenter = { x: 134, y: 75 }

// Maximum movement radius for pupils
const maxPupilOffset = 4

// Calculate pupil positions based on mouse position or natural movement
const leftPupilX = computed(() => {
  const baseX = leftEyeCenter.x + bodyOffset.value.x

  if (isTracking.value) {
    const dx = mouseX.value - leftEyeCenter.x
    const dy = mouseY.value - leftEyeCenter.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance === 0) return baseX

    const limitedDistance = Math.min(distance, maxPupilOffset)
    return baseX + (dx / distance) * limitedDistance
  }

  return baseX + naturalEyeOffset.value.x
})

const leftPupilY = computed(() => {
  const baseY = leftEyeCenter.y + bodyOffset.value.y

  if (isTracking.value) {
    const dx = mouseX.value - leftEyeCenter.x
    const dy = mouseY.value - leftEyeCenter.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance === 0) return baseY

    const limitedDistance = Math.min(distance, maxPupilOffset)
    return baseY + (dy / distance) * limitedDistance
  }

  return baseY + naturalEyeOffset.value.y
})

const rightPupilX = computed(() => {
  const baseX = rightEyeCenter.x + bodyOffset.value.x

  if (isTracking.value) {
    const dx = mouseX.value - rightEyeCenter.x
    const dy = mouseY.value - rightEyeCenter.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance === 0) return baseX

    const limitedDistance = Math.min(distance, maxPupilOffset)
    return baseX + (dx / distance) * limitedDistance
  }

  return baseX + naturalEyeOffset.value.x
})

const rightPupilY = computed(() => {
  const baseY = rightEyeCenter.y + bodyOffset.value.y

  if (isTracking.value) {
    const dx = mouseX.value - rightEyeCenter.x
    const dy = mouseY.value - rightEyeCenter.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance === 0) return baseY

    const limitedDistance = Math.min(distance, maxPupilOffset)
    return baseY + (dy / distance) * limitedDistance
  }

  return baseY + naturalEyeOffset.value.y
})

const handleMouseMove = (event: MouseEvent) => {
  if (!svgRef.value) return

  const rect = svgRef.value.getBoundingClientRect()
  const scale = 232 / rect.width

  mouseX.value = (event.clientX - rect.left) * scale
  mouseY.value = (event.clientY - rect.top) * scale
  isHovering.value = true
  isTracking.value = true
  lastInteractionTime = Date.now()
}

const handleMouseLeave = () => {
  isHovering.value = false
}

const handleClick = () => {
  if (clickAnimationId) {
    cancelAnimationFrame(clickAnimationId)
  }

  const startTime = Date.now()
  const duration = 600

  const animate = () => {
    const elapsed = Date.now() - startTime
    const progress = elapsed / duration

    if (progress < 1) {
      const bounce = Math.sin(progress * Math.PI * 2) * (1 - progress) * 0.3
      bodyScale.value = 1 + bounce

      clickAnimationId = requestAnimationFrame(animate)
    } else {
      bodyScale.value = 1
      clickAnimationId = null
    }
  }

  animate()
}

// Global mouse tracking for when cursor is outside the SVG
const handleGlobalMouseMove = (event: MouseEvent) => {
  if (!svgRef.value) return

  const rect = svgRef.value.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2

  // Calculate angle from SVG center to mouse
  const angle = Math.atan2(event.clientY - centerY, event.clientX - centerX)

  // Set pupils to look towards mouse direction
  const distance = 100 // arbitrary large distance

  mouseX.value = 116 + Math.cos(angle) * distance
  mouseY.value = 98 + Math.sin(angle) * distance
  isTracking.value = true
  lastInteractionTime = Date.now()
}

// Natural floating animation for body and eyes
const startFloatingAnimation = () => {
  animationStartTime = Date.now()

  const animate = () => {
    const elapsed = Date.now() - animationStartTime
    const timeSinceInteraction = Date.now() - lastInteractionTime

    // Body floating animation
    const floatX = Math.sin(elapsed * 0.0008) * 3 + Math.sin(elapsed * 0.0012) * 2
    const floatY = Math.cos(elapsed * 0.001) * 2.5 + Math.sin(elapsed * 0.0015) * 1.5

    bodyOffset.value = {
      x: floatX,
      y: floatY
    }

    // Scale animation immediately
    bodyScale.value = 1 + Math.sin(elapsed * 0.002) * 0.02

    // Natural eye movement when not tracking (after 3 seconds of no interaction)
    if (timeSinceInteraction > 3000) {
      isTracking.value = false

      // Subtle natural eye movements
      const eyeX = Math.sin(elapsed * 0.0005) * 4 + Math.cos(elapsed * 0.0008) * 2
      const eyeY = Math.cos(elapsed * 0.0006) * 3 + Math.sin(elapsed * 0.0009) * 1.5

      naturalEyeOffset.value = {
        x: eyeX,
        y: eyeY
      }
    }

    animationId = requestAnimationFrame(animate)
  }

  animationId = requestAnimationFrame(animate)
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('mousemove', handleGlobalMouseMove)
    // Start animation immediately without delay
    startFloatingAnimation()
  }
})

onUnmounted(() => {
  if (animationId) {
    cancelAnimationFrame(animationId)
  }
  if (clickAnimationId) {
    cancelAnimationFrame(clickAnimationId)
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('mousemove', handleGlobalMouseMove)
  }
})
</script>

<style scoped>
.benjix-round-container {
  display: inline-block;
  cursor: pointer;
  border-radius: 50%;
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: relative;
}

.benjix-round-container svg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Ensure perfect circular clipping across all browsers */
.benjix-round-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  z-index: 10;
  pointer-events: none;
}
</style>
